import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CertificationsSection = () => {
  const certifications = [
    {
      title: "FSC - Forest Stewardship Council",
      description: "Certificação internacional que garante que nossa madeira vem de florestas manejadas de forma responsável e sustentável.",
      icon: "TreePine",
      year: "2012",
      image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
      benefits: [
        "100% madeira certificada",
        "Rastreabilidade completa",
        "Preservação da biodiversidade",
        "Apoio às comunidades locais"
      ]
    },
    {
      title: "ABIMÓVEL - Selo de Qualidade",
      description: "Reconhecimento da Associação Brasileira das Indústrias do Mobiliário pela excelência em qualidade e design.",
      icon: "Award",
      year: "2015",
      image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
      benefits: [
        "Padrões rigorosos de qualidade",
        "Testes de durabilidade",
        "Conformidade com normas técnicas",
        "Garantia estendida"
      ]
    },
    {
      title: "IPHAN - Patrimônio Artesanal",
      description: "Registro no Instituto do Patrimônio Histórico e Artístico Nacional como preservadores de técnicas tradicionais brasileiras.",
      icon: "Shield",
      year: "2018",
      image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
      benefits: [
        "Técnicas tradicionais preservadas",
        "Conhecimento ancestral",
        "Formação de novos artesãos",
        "Valorização cultural"
      ]
    },
    {
      title: "ISO 14001 - Gestão Ambiental",
      description: "Sistema de gestão ambiental que demonstra nosso compromisso com a redução do impacto ambiental em todos os processos.",
      icon: "Leaf",
      year: "2020",
      image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
      benefits: [
        "Redução de resíduos",
        "Eficiência energética",
        "Reciclagem de materiais",
        "Monitoramento contínuo"
      ]
    }
  ];

  const awards = [
    {
      title: "Prêmio Design Brasil",
      year: "2008, 2015, 2020",
      description: "Reconhecimento nacional pela excelência em design de móveis artesanais",
      icon: "Trophy"
    },
    {
      title: "Melhor Móvel Artesanal",
      year: "2019",
      description: "Feira Internacional do Móvel - São Paulo",
      icon: "Star"
    },
    {
      title: "Sustentabilidade Empresarial",
      year: "2021",
      description: "Prêmio FIESP de Mérito Ambiental",
      icon: "Eco"
    },
    {
      title: "Inovação em Marcenaria",
      year: "2023",
      description: "Associação Brasileira de Designers de Interiores",
      icon: "Lightbulb"
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-success/10 text-success px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="Shield" size={16} />
            <span>Certificações & Prêmios</span>
          </div>
          <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-primary mb-6">
            Qualidade Reconhecida
            <span className="block text-secondary">e Certificada</span>
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Nosso compromisso com a excelência é reconhecido por instituições nacionais e internacionais 
            através de certificações rigorosas e prêmios prestigiosos.
          </p>
        </div>

        {/* Certifications Grid */}
        <div className="grid lg:grid-cols-2 gap-12 mb-20">
          {certifications?.map((cert, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-muted/50 to-white rounded-3xl p-8 shadow-premium border border-border hover:shadow-premium-lg transition-all duration-300"
            >
              <div className="flex items-start space-x-6">
                {/* Icon & Year */}
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gradient-forest rounded-2xl flex items-center justify-center mb-4">
                    <Icon name={cert?.icon} size={32} color="white" strokeWidth={1.5} />
                  </div>
                  <div className="text-center">
                    <div className="bg-accent text-accent-foreground px-3 py-1 rounded-full text-sm font-bold">
                      {cert?.year}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1">
                  <h3 className="font-playfair text-xl font-bold text-primary mb-3">
                    {cert?.title}
                  </h3>
                  <p className="text-text-secondary mb-6 leading-relaxed">
                    {cert?.description}
                  </p>

                  {/* Benefits */}
                  <div className="space-y-2">
                    {cert?.benefits?.map((benefit, benefitIndex) => (
                      <div key={benefitIndex} className="flex items-center space-x-3">
                        <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                        <span className="text-sm text-text-secondary">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Certification Image */}
              <div className="mt-6 relative overflow-hidden rounded-xl">
                <Image
                  src={cert?.image}
                  alt={`Certificação ${cert?.title}`}
                  className="w-full h-32 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-transparent"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Awards Section */}
        <div className="bg-gradient-to-br from-accent/5 to-primary/5 rounded-3xl p-12">
          <div className="text-center mb-12">
            <Icon name="Trophy" size={48} color="var(--color-accent)" className="mx-auto mb-6" />
            <h3 className="font-playfair text-3xl font-bold text-primary mb-4">
              Prêmios & Reconhecimentos
            </h3>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Nossa dedicação à excelência artesanal é reconhecida através de diversos prêmios 
              e menções honrosas no setor moveleiro brasileiro.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {awards?.map((award, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-premium border border-border text-center hover:shadow-premium-lg transition-all duration-300 group"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-accent/20 to-primary/20 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Icon name={award?.icon} size={24} color="var(--color-primary)" strokeWidth={2} />
                </div>
                
                <h4 className="font-playfair text-lg font-bold text-primary mb-2">
                  {award?.title}
                </h4>
                
                <div className="text-accent font-bold text-sm mb-3">
                  {award?.year}
                </div>
                
                <p className="text-text-secondary text-sm leading-relaxed">
                  {award?.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-20 grid md:grid-cols-3 gap-8 text-center">
          <div className="space-y-4">
            <div className="w-16 h-16 bg-gradient-forest rounded-2xl flex items-center justify-center mx-auto">
              <Icon name="Shield" size={32} color="white" strokeWidth={1.5} />
            </div>
            <h4 className="font-playfair text-xl font-bold text-primary">Garantia Vitalícia</h4>
            <p className="text-text-secondary">
              Todos os nossos móveis possuem garantia vitalícia contra defeitos de fabricação
            </p>
          </div>

          <div className="space-y-4">
            <div className="w-16 h-16 bg-gradient-forest rounded-2xl flex items-center justify-center mx-auto">
              <Icon name="Recycle" size={32} color="white" strokeWidth={1.5} />
            </div>
            <h4 className="font-playfair text-xl font-bold text-primary">100% Sustentável</h4>
            <p className="text-text-secondary">
              Utilizamos apenas madeiras certificadas e práticas ambientalmente responsáveis
            </p>
          </div>

          <div className="space-y-4">
            <div className="w-16 h-16 bg-gradient-forest rounded-2xl flex items-center justify-center mx-auto">
              <Icon name="Users" size={32} color="white" strokeWidth={1.5} />
            </div>
            <h4 className="font-playfair text-xl font-bold text-primary">Artesãos Certificados</h4>
            <p className="text-text-secondary">
              Nossa equipe possui certificações técnicas e anos de experiência comprovada
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CertificationsSection;